import { motion } from 'framer-motion';
import { ArrowRight, Shield, BarChart3, CheckCircle2, Mail, Users, TrendingUp, Zap } from 'lucide-react';

const PrivateClients = () => {
  const tools = [
    {
      icon: <Zap className="w-12 h-12 text-accent-500" />,
      title: "White-labelled Tools for Private Clients",
      description: "Use digital tools customised to your investment goals, ensuring seamless interaction with funding opportunities",
    },
    {
      icon: <Shield className="w-12 h-12 text-accent-500" />,
      title: "Eligibility & Affordability Assessments",
      description: "Assess funding options based on risk models, ensuring investments match your preferences",
    },
    {
      icon: <CheckCircle2 className="w-12 h-12 text-accent-500" />,
      title: "Secure Access & Compliance",
      description: "Submit proposals and monitor funding activity with secure, real-time compliance checks",
    },
  ];

  const insights = [
    "Loan book performance",
    "Risk exposure",
    "Opportunity hotspots",
    "Affordability trends",
  ];

  const benefits = [
    "Expert guidance on SME funding opportunities",
    "Tailored investment recommendations",
    "Secure, streamlined access to deals",
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-900 leading-tight">
                Private{' '}
                <span className="gradient-text">Clients</span>
              </h1>
              <p className="text-xl text-gray-600 font-medium">
                SME lending solutions and digital insights designed for private investors and business owners.
              </p>
              <p className="text-gray-600 leading-relaxed">
                At 1ST TRUST FINANCES, our technology empowers private clients to access funding solutions, monitor portfolio performance, and make data-driven investment decisions.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="privateGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#privateGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="80" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="160" height="50" rx="5" fill="#0056d6" />
                  <Users className="absolute" x="180" y="60" width="50" height="50" fill="#0ea5e9" opacity="0.3" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="overviewPrivateGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#overviewPrivateGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <circle cx="150" cy="100" r="50" fill="#0056d6" opacity="0.2" />
                  <circle cx="80" cy="150" r="35" fill="#0ea5e9" opacity="0.2" />
                  <circle cx="220" cy="150" r="35" fill="#0056d6" opacity="0.15" />
                  <rect x="50" y="200" width="200" height="80" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="230" width="100" height="20" rx="5" fill="#e6f0ff" />
                </g>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Lending Solutions Tailored for You
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Our digital lending platform helps private clients:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-gray-600">
                  <ArrowRight className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" />
                  Access SME funding opportunities
                </li>
                <li className="flex items-start text-gray-600">
                  <ArrowRight className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" />
                  Evaluate risk and portfolio performance
                </li>
                <li className="flex items-start text-gray-600">
                  <ArrowRight className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" />
                  Stay informed with real-time insights
                </li>
                <li className="flex items-start text-gray-600">
                  <ArrowRight className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" />
                  Streamline funding applications
                </li>
              </ul>
              <p className="text-gray-600 leading-relaxed">
                This ensures private investors can participate confidently in SME lending while maximising returns.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intelligent Tools Section */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900 mb-4">
              Intelligent Tools for Private Clients
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Powerful tools designed for your investment needs.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {tools.map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-6">{tool.icon}</div>
                <h3 className="font-heading text-xl font-bold text-primary-900 mb-3">
                  {tool.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {tool.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Real-time Portfolio & Risk Insights */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Monitor Your Portfolio & Risk in Real-Time
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Private clients gain insights into:
              </p>
              <div className="bg-gradient-to-br from-primary-50 to-white rounded-xl p-6 shadow-lg">
                <ul className="space-y-3">
                  {insights.map((insight, index) => (
                    <li key={index} className="flex items-start text-gray-600">
                      <ArrowRight className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" />
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Combining macro trends with individual client-level analysis ensures informed decision-making.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="portfolioGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#portfolioGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <rect x="50" y="50" width="200" height="200" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="80" width="80" height="20" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="120" width="140" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="150" width="120" height="15" rx="5" fill="#e6f0ff" />
                  <rect x="70" y="180" width="160" height="50" rx="5" fill="#0056d6" />
                  <BarChart3 className="absolute" x="180" y="60" width="50" height="50" fill="#0ea5e9" opacity="0.3" />
                </g>
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Private Client Engagement & Support */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <svg viewBox="0 0 500 400" className="w-full h-auto">
                <defs>
                  <linearGradient id="engagementGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0056d6" />
                    <stop offset="100%" stopColor="#0ea5e9" />
                  </linearGradient>
                </defs>
                <circle cx="250" cy="200" r="150" fill="url(#engagementGradient)" opacity="0.1" />
                <g transform="translate(100, 80)">
                  <circle cx="150" cy="100" r="50" fill="#0056d6" opacity="0.2" />
                  <circle cx="80" cy="150" r="35" fill="#0ea5e9" opacity="0.2" />
                  <circle cx="220" cy="150" r="35" fill="#0056d6" opacity="0.15" />
                  <rect x="50" y="200" width="200" height="80" rx="10" fill="white" stroke="#0056d6" strokeWidth="2" />
                  <rect x="70" y="230" width="100" height="20" rx="5" fill="#e6f0ff" />
                </g>
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-900">
                Dedicated Support for Private Clients
              </h2>
              <p className="text-gray-600 leading-relaxed">
                1ST TRUST FINANCES provides private clients with:
              </p>
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start text-gray-600">
                      <ArrowRight className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Our goal is to make private client participation in SME lending seamless, informed, and profitable.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - Contact Form */}
      <section className="section gradient-bg text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 1000 1000" className="w-full h-full">
            <defs>
              <pattern id="grid7" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid7)" />
          </svg>
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Start Accessing SME Funding Today
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Connect with our team to learn more about our private client services.
            </p>
            <form className="bg-white rounded-xl p-8 shadow-2xl">
              <div className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-left text-gray-700 font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary w-full"
                >
                  FIND OUT MORE
                  <ArrowRight className="inline ml-2 w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                By submitting, you acknowledge our privacy policy and understand a member of 1ST TRUST FINANCES team will contact you.
              </p>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivateClients;
